import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnType } from "antd/es/table/interface";

import QueryTable from "@/components_v2/Tables/QueryTable";
import { useLazyGetCertificationInvitationsQuery } from "@/redux/service";
import type { CertificationInvitation } from "@/redux/certifications/types";
import StatusChip from "@/domains/certifications/components/StatusChip";
import { INVITATION_STATUSES } from "@/domains/certifications/constants";
import { formatShortCertificationDate } from "@/domains/certifications/utils";
import InvitationAction from "@/features/certifications/invitations/components/InvitationAction";
import InvitationFilters from "@/features/certifications/invitations/components/InvitationFilters";
import {
  InvitationFiltersState,
  InvitationsTabProps,
} from "@/features/certifications/invitations/types";

import "./styles.css";
import { certificationRouteKey } from "@/domains/certifications/routes";

const InvitationsTab = ({ certification }: InvitationsTabProps) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<InvitationFiltersState>({});
  const queryFilters = useMemo(
    () => ({ ...filters, certificationSlug: certification.slug }),
    [filters, certification.slug],
  );

  const columns: ColumnType<CertificationInvitation>[] = useMemo(
    () => [
      {
        key: "participant",
        title: t("certification.admin.invitations.columns.participant"),
        render: (_, invitation) => (
          <span className="invitations-email body-s-regular">
            {invitation.email}
          </span>
        ),
      },
      {
        key: "sent",
        title: t("certification.admin.invitations.columns.sent"),
        render: (_, invitation) => (
          <span className="body-s-regular">
            {formatShortCertificationDate(invitation.createdAt)}
          </span>
        ),
      },
      {
        key: "status",
        title: t("certification.admin.invitations.columns.status"),
        render: (_, invitation) => (
          <StatusChip status={INVITATION_STATUSES[invitation.status]} />
        ),
      },
      {
        key: "action",
        align: "right",
        render: (_, invitation) => (
          <InvitationAction
            certificationSlug={certification.slug}
            routeKey={certificationRouteKey(certification)}
            invitation={invitation}
          />
        ),
      },
    ],
    [certification.slug, t],
  );

  return (
    <div className="table-container-layout">
      <InvitationFilters setFilters={setFilters} />

      <QueryTable<CertificationInvitation>
        query={useLazyGetCertificationInvitationsQuery}
        filters={queryFilters}
        columns={columns}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t("certification.admin.invitations.empty") }}
      />
    </div>
  );
};

export default InvitationsTab;
